import { useEffect, useRef, useState } from "react";
import { IonButton, IonIcon } from "@ionic/react";
import {
  locationOutline,
  checkmarkOutline,
  alertOutline,
} from "ionicons/icons";

import "./LocationButton.css";

type ButtonState = "ready" | "processing" | "success" | "failure";

interface LocationButtonProps {
  onDrop: () => Promise<void>;
  disabled?: boolean;
}

const icons = {
  ready: locationOutline,
  processing: locationOutline,
  success: checkmarkOutline,
  failure: alertOutline,
};

const messages = {
  ready: "",
  processing: "Saving breadcrumb.",
  success: "Location saved.",
  failure: "Location could not be saved. Please try again.",
};

export default function LocationButton({
  onDrop,
  disabled = false,
}: LocationButtonProps) {
  const [state, setState] = useState<ButtonState>("ready");

  const mounted = useRef(false);
  const processing = useRef(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;

      if (resetTimer.current !== null) {
        clearTimeout(resetTimer.current);
      }
    };
  }, []);

  async function handleDrop() {
    // The ref also blocks rapid taps before React renders again.
    if (disabled || processing.current) return;

    if (resetTimer.current !== null) {
      clearTimeout(resetTimer.current);
      resetTimer.current = null;
    }

    processing.current = true;
    setState("processing");

    let result: ButtonState = "success";

    try {
      await onDrop();
    } catch {
      result = "failure";
    } finally {
      processing.current = false;
    }

    if (!mounted.current) return;

    setState(result);

    resetTimer.current = setTimeout(() => {
      resetTimer.current = null;

      if (mounted.current) {
        setState("ready");
      }
    }, 5000);
  }

  const isProcessing = state === "processing";

  return (
    <div className="breadcrumb-control" data-state={state}>
      <div className="breadcrumb-control__target">
        {isProcessing && (
          <div className="breadcrumb-control__rings" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
        )}

        <IonButton
          className="breadcrumb-control__button"
          type="button"
          fill="solid"
          disabled={disabled || isProcessing}
          aria-label="Drop a breadcrumb"
          aria-busy={isProcessing}
          onClick={handleDrop}
        >
          <IonIcon
            slot="icon-only"
            icon={icons[state]}
            aria-hidden="true"
          />
        </IonButton>
      </div>

      <span
        className="breadcrumb-control__status"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {messages[state]}
      </span>
    </div>
  );
}